import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  lazy,
  Suspense,
} from "react";
import { useTranslation } from "react-i18next";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useThemedCharts } from "../../hooks/useThemedCharts";
import { attachPointContextMenu } from "./pointContextMenu";
import { useThemeClasses } from "../../providers/ThemeProvider";
import { useChartCommentPopover } from "./ChartCommentPanel";
import { useChartComments } from "../../hooks/useChartComments";
import type { ChartComment } from "../../services/ChartCommentService";

const ChartCommentPopover = lazy(() => import("./ChartCommentPanel"));

// Import and initialize mouse wheel zoom module for Highcharts Core
import "highcharts/modules/mouse-wheel-zoom";

// Import exporting modules for Highcharts
import "highcharts/modules/exporting";
import "highcharts/modules/offline-exporting";
import "highcharts/modules/export-data";

// Deep merge function for options
const deepMerge = (target: any, source: any): any => {
  const result = { ...target };
  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      result[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
};

export interface BaseChartProps {
  /** Chart type */
  type?: "line" | "column" | "bar" | "area" | "pie" | "scatter";
  /** Chart title */
  title?: string;
  /** Chart subtitle */
  subtitle?: string;
  /** Chart height in pixels */
  height?: number;
  /** X-axis categories or data */
  categories?: string[];
  /** Y-axis configuration */
  yAxis?: {
    title?: string;
    min?: number;
    max?: number;
    format?: string;
    plotLines?: Array<{
      value: number;
      color?: string;
      dashStyle?: "Solid" | "Dash" | "Dot";
      width?: number;
      label?: string;
    }>;
  };
  /** Chart series data */
  series: Array<{
    name: string;
    data: (number | null)[];
    color?: string;
    type?: "line" | "column" | "bar" | "area" | "pie" | "scatter";
  }>;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Custom CSS class */
  className?: string;
  /** Legend configuration */
  legend?: {
    enabled?: boolean;
    position?: "top" | "bottom" | "left" | "right";
  };
  /** Tooltip customization */
  tooltip?: {
    suffix?: string;
    prefix?: string;
    formatter?: () => string;
  };
  /** Custom chart options to override defaults */
  customOptions?: Partial<Highcharts.Options>;
  /** Pan and zoom configuration */
  panZoom?: {
    enabled?: boolean;
    type?: "x" | "y" | "xy";
    mouseWheel?: boolean;
    pinchZoom?: boolean;
  };
  /** Navigator chart configuration (like Highcharts Stock) */
  navigator?: {
    enabled?: boolean;
    height?: number;
    margin?: number;
    maskFill?: string;
    outlineColor?: string;
    outlineWidth?: number;
    handleColor?: string;
    handleBorderColor?: string;
  };
  /** Callback for chart selection change (for navigator interaction) */
  onSelectionChange?: (min: number, max: number) => void;
  /** Current selection range (for navigator highlighting) */
  selectionRange?: { min: number; max: number };
  /** Callback for retry action */
  onRetry?: () => void;
  /** No data message */
  noDataMessage?: string;
  /** Export functionality configuration */
  exporting?: {
    enabled?: boolean;
    filename?: string;
  };
  /** Extra controls to render on the left side of the button row */
  extraControls?: React.ReactNode;
  /** Chart comment props — if chartType is set, the comment button appears */
  commentProps?: {
    chartType: string;
    machineBK?: string;
    shifts?: string[];
    timestampMode?: boolean;
  };
}

const BaseChart: React.FC<BaseChartProps> = ({
  type = "column",
  title,
  subtitle,
  height = 400,
  categories = [],
  yAxis = {},
  series = [],
  loading = false,
  error = null,
  className = "",
  legend = { enabled: true, position: "right" },
  tooltip = {},
  customOptions = {},
  panZoom = { enabled: true, type: "x", mouseWheel: true, pinchZoom: true },
  navigator = {
    enabled: false,
    height: 80,
    margin: 20,
    maskFill: "rgba(37, 99, 235, 0.15)",
    outlineColor: "#2563eb",
    outlineWidth: 2,
    handleColor: "#2563eb",
    handleBorderColor: "#1d4ed8",
  },
  onSelectionChange,
  selectionRange,
  onRetry,
  noDataMessage = "No data available",
  exporting = { enabled: true, filename: "chart" },
  extraControls,
  commentProps,
}) => {
  const { popoverPoint, openPopover, closePopover } = useChartCommentPopover();
  const { comments: chartComments, refetch: refetchComments } =
    useChartComments({
      chartType: commentProps?.chartType ?? "",
      machineBK: commentProps?.machineBK,
    });

  const { t } = useTranslation();

  // Export functions for raw data
  const exportToCSV = () => {
    const csvContent = generateCSVContent();
    downloadFile(
      csvContent,
      `${exporting.filename || "chart"}.csv`,
      "text/csv",
    );
  };

  const exportToExcel = () => {
    const csvContent = generateCSVContent();
    downloadFile(
      csvContent,
      `${exporting.filename || "chart"}.xls`,
      "application/vnd.ms-excel",
    );
  };

  const generateCSVContent = () => {
    // Create CSV header
    const headers = ["Category", ...series.map((s) => s.name)];
    let csvContent = headers.join(",") + "\n";

    // Add data rows
    categories.forEach((category, index) => {
      const row = [category];
      series.forEach((s) => {
        const value = s.data[index];
        row.push(value !== null && value !== undefined ? value.toString() : "");
      });
      csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
    });

    return csvContent;
  };

  const downloadFile = (
    content: string,
    filename: string,
    mimeType: string,
  ) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Get themed chart configurations
  const { getChartConfig, getSeriesColor, getStatusColors, isDark } =
    useThemedCharts();
  const themeClasses = useThemeClasses();
  const statusColors = getStatusColors;

  // Theme-aware navigator defaults
  // Memoised because `chartOptions` depends on it: as a bare object literal it
  // got a new identity on every render, so any local state change (opening the
  // comment popover, toggling the data table) rebuilt the options and made
  // Highcharts redraw the whole chart.
  const themedNavigator = useMemo(
    () => ({
      enabled: false,
      height: 80,
      margin: 20,
      maskFill: isDark ? "rgba(59, 130, 246, 0.15)" : "rgba(37, 99, 235, 0.15)",
      outlineColor: statusColors.info,
      outlineWidth: 2,
      handleColor: statusColors.info,
      handleBorderColor: isDark ? "#1d4ed8" : "#1d4ed8",
      ...navigator,
    }),
    [isDark, statusColors.info, navigator],
  );

  // State for navigator selection - default to full range when no selection
  const [navigatorSelection, setNavigatorSelection] = useState<{
    min: number;
    max: number;
  } | null>(selectionRange || null);
  const [showData, setShowData] = useState(false);

  // Navigator chart ref for interactions
  const navigatorChartRef = useRef<any>(null);
  const mainChartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const commentMarkersRef = useRef<any[]>([]);

  // Update navigator selection when prop changes
  useEffect(() => {
    if (selectionRange) {
      setNavigatorSelection(selectionRange);
    }
  }, [selectionRange]);

  // Initialize navigator selection to full range when categories change and navigator is enabled
  useEffect(() => {
    if (
      themedNavigator.enabled &&
      categories.length > 0 &&
      !navigatorSelection &&
      !selectionRange
    ) {
      const fullRange = { min: 0, max: categories.length - 1 };
      setNavigatorSelection(fullRange);
    }
  }, [categories, themedNavigator.enabled, navigatorSelection, selectionRange]);

  // Handle navigator selection
  const handleNavigatorSelection = useCallback(
    (min: number, max: number) => {
      const newSelection = { min, max };
      setNavigatorSelection(newSelection);
      onSelectionChange?.(min, max);

      // Update main chart zoom to match navigator selection
      if (mainChartRef.current) {
        const chart = mainChartRef.current.chart;
        if (chart && chart.xAxis && chart.xAxis[0]) {
          chart.xAxis[0].setExtremes(min, max, true, false);
        }
      }

      // Update navigator chart with new selection
      if (navigatorChartRef.current) {
        const navigatorChart = navigatorChartRef.current.chart;
        if (navigatorChart && navigatorChart.xAxis && navigatorChart.xAxis[0]) {
          // Remove existing plot bands and lines
          const xAxis = navigatorChart.xAxis[0];
          if (xAxis.plotLinesAndBands) {
            xAxis.plotLinesAndBands.forEach((band: any) => {
              if (
                band.options.className === "navigator-selection" ||
                band.options.className === "navigator-handle"
              ) {
                band.destroy();
              }
            });
          }

          // Add new selection highlighting
          xAxis.addPlotBand({
            from: min,
            to: max,
            color: navigator.maskFill || "rgba(37, 99, 235, 0.15)",
            borderColor: navigator.outlineColor || "#2563eb",
            borderWidth: navigator.outlineWidth || 2,
            className: "navigator-selection",
            zIndex: 3,
          });

          // Add handle lines
          xAxis.addPlotLine({
            value: min,
            color: navigator.handleColor || "#2563eb",
            width: 3,
            zIndex: 4,
            className: "navigator-handle",
            label: {
              text: "◀",
              style: {
                color: navigator.handleColor || "#2563eb",
                fontSize: "12px",
                fontWeight: "bold",
              },
              verticalAlign: "middle",
              y: -5,
            },
          });

          xAxis.addPlotLine({
            value: max,
            color: navigator.handleColor || "#2563eb",
            width: 3,
            zIndex: 4,
            className: "navigator-handle",
            label: {
              text: "▶",
              style: {
                color: navigator.handleColor || "#2563eb",
                fontSize: "12px",
                fontWeight: "bold",
              },
              verticalAlign: "middle",
              y: -5,
            },
          });
        }
      }
    },
    [onSelectionChange, navigator],
  );

  // Keyboard navigation support for navigator
  useEffect(() => {
    if (!navigator.enabled || !containerRef.current) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!navigatorSelection) return;

      const currentRange = navigatorSelection.max - navigatorSelection.min;
      const step = Math.max(1, Math.floor(currentRange * 0.1)); // 10% of current range

      switch (event.key) {
        case "ArrowLeft":
          // Move selection left
          event.preventDefault();
          const newMinLeft = Math.max(0, navigatorSelection.min - step);
          const newMaxLeft = newMinLeft + currentRange;
          if (newMaxLeft <= categories.length - 1) {
            handleNavigatorSelection(newMinLeft, newMaxLeft);
          }
          break;

        case "ArrowRight":
          // Move selection right
          event.preventDefault();
          const newMaxRight = Math.min(
            categories.length - 1,
            navigatorSelection.max + step,
          );
          const newMinRight = newMaxRight - currentRange;
          if (newMinRight >= 0) {
            handleNavigatorSelection(newMinRight, newMaxRight);
          }
          break;

        case "ArrowUp":
          // Expand selection
          event.preventDefault();
          const expansion = Math.max(1, Math.floor(currentRange * 0.2));
          const expandedMin = Math.max(0, navigatorSelection.min - expansion);
          const expandedMax = Math.min(
            categories.length - 1,
            navigatorSelection.max + expansion,
          );
          handleNavigatorSelection(expandedMin, expandedMax);
          break;

        case "ArrowDown":
          // Contract selection
          event.preventDefault();
          const contraction = Math.max(1, Math.floor(currentRange * 0.2));
          const contractedMin = navigatorSelection.min + contraction;
          const contractedMax = navigatorSelection.max - contraction;
          if (contractedMin < contractedMax) {
            handleNavigatorSelection(contractedMin, contractedMax);
          }
          break;

        case "Escape":
          // Reset to full range selection
          event.preventDefault();
          const fullRange = { min: 0, max: categories.length - 1 };
          setNavigatorSelection(fullRange);
          onSelectionChange?.(0, categories.length - 1);
          if (mainChartRef.current) {
            const chart = mainChartRef.current.chart;
            if (chart && chart.xAxis && chart.xAxis[0]) {
              chart.xAxis[0].setExtremes(null, null, true, false);
            }
          }
          break;
      }
    };

    const container = containerRef.current;
    container.addEventListener("keydown", handleKeyDown);
    container.setAttribute("tabindex", "0"); // Make container focusable

    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    themedNavigator.enabled,
    navigatorSelection,
    categories.length,
    handleNavigatorSelection,
    onSelectionChange,
  ]);

  // Track when chart is loaded
  const [chartLoaded, setChartLoaded] = useState(false);

  // ─── Point RIGHT-click → open comment popover ───
  // Highcharts has no point-level contextmenu event, so the listener sits on the
  // container and resolves the point under the cursor. Left-click stays free for
  // zoom/selection.
  useEffect(() => {
    const chart = mainChartRef.current?.chart;
    if (!chart || !commentProps?.chartType) return;

    return attachPointContextMenu(chart, (point) => {
      const c = point.series.chart;
      const p = point as unknown as {
        plotX?: number;
        plotY?: number;
        category?: unknown;
        x?: unknown;
      };
      const plotX = (p.plotX ?? 0) + c.plotLeft;
      const plotY = (p.plotY ?? 0) + c.plotTop;
      const category = String(p.category ?? p.x ?? "");
      const seriesName = String(point.series.name ?? "");

      // Category may carry both date and shift ("2023-01-12 A1"); otherwise the
      // shift comes from the series name ("Efficiency A1").
      let shiftDate: string;
      let shiftName: string | undefined;
      const catParts = category.split(" ");
      if (catParts.length >= 2) {
        shiftDate = catParts[0];
        shiftName = catParts.slice(1).join(" ");
      } else {
        shiftDate = category;
        const shiftMatch = seriesName.match(/\s([A-Z]\d*)$/i);
        shiftName = shiftMatch ? shiftMatch[1] : undefined;
      }

      openPopover({
        x: plotX,
        y: plotY,
        category,
        seriesName,
        shiftDate,
        shiftName,
      });
    });
  }, [commentProps?.chartType, openPopover, chartLoaded]);

  // ─── Comment markers: draw 💬 icons on chart at positions with comments ───
  useEffect(() => {
    const chart = mainChartRef.current?.chart;
    if (!chart || !commentProps?.chartType) return;

    const drawMarkers = () => {
      // Clean up previous markers
      commentMarkersRef.current.forEach((m) => {
        try {
          m.destroy();
        } catch {}
      });
      commentMarkersRef.current = [];

      if (!chartComments.length) return;

      const xAxis = chart.xAxis?.[0];
      if (!xAxis) return;
      const cats: string[] | undefined = (xAxis as any).categories;
      if (!cats?.length) return;

      // Helper to normalize date for comparison (strip time portion)
      const normalizeDate = (d: string | undefined) => d?.split("T")[0] || "";

      // Group comments by category index + shift name
      // Key format: "catIdx|shiftName"
      const grouped = new Map<string, ChartComment[]>();
      for (const c of chartComments) {
        const shiftDate = normalizeDate(c.shift_date);
        const shiftName = c.shift_name || "";
        const timestamp = c.timestamp || "";

        // Find matching category
        const idx = cats.findIndex((cat) => {
          // For shift-based: category like "2026-05-02" or "May 02"
          if (shiftDate) {
            // Category matches date exactly or contains it
            const catNorm = normalizeDate(cat);
            return (
              catNorm === shiftDate ||
              cat.includes(shiftDate) ||
              shiftDate.includes(cat)
            );
          }
          // For timestamp-based
          if (timestamp) {
            return (
              cat === timestamp ||
              cat.includes(timestamp) ||
              timestamp.includes(cat)
            );
          }
          return false;
        });
        if (idx < 0) continue;
        const key = `${idx}|${shiftName}`;
        const arr = grouped.get(key) || [];
        arr.push(c);
        grouped.set(key, arr);
      }

      // Collect all unique shifts at each category to calculate offset
      const shiftsAtCat = new Map<number, string[]>();
      grouped.forEach((_, compositeKey) => {
        const [catIdxStr, shiftName] = compositeKey.split("|");
        const catIdx = parseInt(catIdxStr, 10);
        if (!shiftsAtCat.has(catIdx)) shiftsAtCat.set(catIdx, []);
        if (shiftName && !shiftsAtCat.get(catIdx)!.includes(shiftName)) {
          shiftsAtCat.get(catIdx)!.push(shiftName);
        }
      });

      grouped.forEach((cmts, compositeKey) => {
        const [catIdxStr, shiftName] = compositeKey.split("|");
        const catIdx = parseInt(catIdxStr, 10);
        let pixelX = xAxis.toPixels(catIdx, false);

        // Offset markers horizontally if multiple shifts on same category
        const shifts = shiftsAtCat.get(catIdx) || [];
        if (shiftName && shifts.length > 1) {
          const shiftIdx = shifts.sort().indexOf(shiftName);
          const totalShifts = shifts.length;
          const spacing = 28; // pixels between shift markers
          const totalWidth = (totalShifts - 1) * spacing;
          pixelX = pixelX - totalWidth / 2 + shiftIdx * spacing;
        }

        const count = cmts.length;

        const group = chart.renderer
          .g("comment-marker")
          .attr({ zIndex: 10 })
          .add();

        // Light grey dashed vertical line spanning full chart height
        chart.renderer
          .path([
            "M",
            pixelX,
            chart.plotTop,
            "L",
            pixelX,
            chart.plotTop + chart.plotHeight,
          ] as any)
          .attr({
            stroke: isDark ? "#64748b" : "#cbd5e1",
            "stroke-width": 1,
            "stroke-dasharray": "4,4",
            zIndex: 5,
          })
          .add(group);

        // Chat bubble icon - neutral/grey style
        const iconY = chart.plotTop + 4;
        chart.renderer
          .path([
            "M",
            pixelX - 8,
            iconY + 2,
            "L",
            pixelX + 8,
            iconY + 2,
            "Q",
            pixelX + 11,
            iconY + 2,
            pixelX + 11,
            iconY + 5,
            "L",
            pixelX + 11,
            iconY + 14,
            "Q",
            pixelX + 11,
            iconY + 17,
            pixelX + 8,
            iconY + 17,
            "L",
            pixelX - 3,
            iconY + 17,
            "L",
            pixelX - 6,
            iconY + 21,
            "L",
            pixelX - 5,
            iconY + 17,
            "L",
            pixelX - 8,
            iconY + 17,
            "Q",
            pixelX - 11,
            iconY + 17,
            pixelX - 11,
            iconY + 14,
            "L",
            pixelX - 11,
            iconY + 5,
            "Q",
            pixelX - 11,
            iconY + 2,
            pixelX - 8,
            iconY + 2,
            "Z",
          ] as any)
          .attr({
            fill: isDark ? "#475569" : "#64748b",
            cursor: "pointer",
          })
          .add(group);

        // Comment count or icon inside
        chart.renderer
          .text(count > 1 ? String(count) : "✉", pixelX, iconY + 13)
          .attr({ align: "center" })
          .css({
            fontSize: count > 1 ? "10px" : "11px",
            fontWeight: "bold",
            color: "#fff",
            cursor: "pointer",
          })
          .add(group);

        // Tooltip on hover - show comments in chat-like format
        const shiftLabel = shiftName ? `Shift ${shiftName}` : "";
        const tooltipLines = cmts.map((c) => {
          const authorName = (c.author || "Unknown").split("@")[0];
          return `${authorName}: ${c.text}`;
        });
        const tooltipText =
          (shiftLabel ? `${shiftLabel}\\n` : "") + tooltipLines.join("\\n");
        let tooltip: any = null;

        group.on("mouseover", function () {
          const padding = 10;

          tooltip = chart.renderer
            .label(
              tooltipText,
              pixelX + 15,
              iconY,
              "callout",
              undefined,
              undefined,
              false,
              false,
              "comment-tooltip",
            )
            .attr({
              fill: isDark ? "#1e293b" : "#ffffff",
              stroke: isDark ? "#475569" : "#cbd5e1",
              "stroke-width": 1,
              r: 6,
              padding: padding,
              zIndex: 20,
            })
            .css({
              fontSize: "12px",
              color: isDark ? "#e2e8f0" : "#334155",
              width: "280px",
              whiteSpace: "normal",
            })
            .add();
        });

        group.on("mouseout", function () {
          if (tooltip) {
            tooltip.destroy();
            tooltip = null;
          }
        });

        group.on("click", () => {
          // Destroy tooltip on click
          if (tooltip) {
            tooltip.destroy();
            tooltip = null;
          }

          const first = cmts[0];
          openPopover({
            x: pixelX,
            y: iconY + 30,
            category: cats[catIdx] || "",
            seriesName: shiftName ? `Shift ${shiftName}` : "",
            shiftDate: first.shift_date?.split("T")[0],
            shiftName: first.shift_name || shiftName,
            timestamp: first.timestamp,
          });
        });

        commentMarkersRef.current.push(group);
      });
    };

    drawMarkers();
    Highcharts.addEvent(chart, "redraw", drawMarkers);
    Highcharts.addEvent(chart, "render", drawMarkers);

    // Also trigger on chart load completion
    const onLoad = () => {
      setChartLoaded(true);
      drawMarkers();
    };
    Highcharts.addEvent(chart, "load", onLoad);

    return () => {
      commentMarkersRef.current.forEach((m) => {
        try {
          m.destroy();
        } catch {}
      });
      commentMarkersRef.current = [];
      Highcharts.removeEvent(chart, "redraw", drawMarkers);
      Highcharts.removeEvent(chart, "render", drawMarkers);
      Highcharts.removeEvent(chart, "load", onLoad);
    };
  }, [
    chartComments,
    isDark,
    commentProps?.chartType,
    openPopover,
    chartLoaded,
  ]);

  const chartOptions: Highcharts.Options = useMemo(() => {
    // Get themed base configuration
    const themedConfig = getChartConfig({
      chart: {
        type: type as any,
        height: height,
        // Enable pan and zoom functionality (configurable)
        panKey: "shift",
        panning: {
          enabled: panZoom.enabled,
          type: panZoom.type || "x",
        },
        zooming: {
          type: panZoom.type || "x",
          mouseWheel: {
            enabled: panZoom.mouseWheel !== false,
            sensitivity: 1.1,
          },
          pinchType:
            panZoom.pinchZoom !== false ? panZoom.type || "x" : undefined,
          resetButton: {
            theme: {
              fill: isDark ? "#3b82f6" : "#2563eb",
              stroke: isDark ? "#1d4ed8" : "#1d4ed8",
              style: {
                color: "white",
                fontWeight: "bold",
                fontSize: "12px",
              },
              states: {
                hover: {
                  fill: isDark ? "#1d4ed8" : "#1d4ed8",
                  stroke: isDark ? "#1e40af" : "#1e40af",
                },
              },
            },
            position: {
              align: "right",
              verticalAlign: "top",
              x: -10,
              y: 10,
            },
          },
        },
        // Add events for navigator synchronization
        events: {
          selection: function (event: any) {
            if (event.xAxis && navigator.enabled) {
              const min = event.xAxis[0].min;
              const max = event.xAxis[0].max;
              handleNavigatorSelection(min, max);
              return false; // Prevent default selection behavior
            }
            return true;
          },
        },
      },
      title: {
        text: title,
      },
      subtitle: {
        text: subtitle,
      },
      xAxis: {
        categories,
        labels: {
          rotation: categories.length > 10 ? -45 : 0,
          style: {
            color: statusColors.yAxisLabels,
            fontSize: "12px",
          },
        },
        title: {
          style: {
            color: statusColors.yAxisTitle,
            fontSize: "13px",
            fontWeight: "600",
          },
        },
        // Set initial zoom range if navigator selection exists
        ...(navigatorSelection && {
          min: navigatorSelection.min,
          max: navigatorSelection.max,
        }),
      },
      yAxis: {
        min: yAxis.min ?? 0,
        max: yAxis.max,
        title: {
          text: yAxis.title,
          style: {
            color: statusColors.yAxisTitle,
            fontSize: "13px",
            fontWeight: "600",
          },
        },
        labels: {
          format: yAxis.format ?? "{value}",
          style: {
            color: statusColors.yAxisLabels,
            fontSize: "12px",
          },
        },
        plotLines: yAxis.plotLines?.map((plotLine) => ({
          value: plotLine.value,
          color: plotLine.color ?? (isDark ? "#64748b" : "#64748b"),
          dashStyle: plotLine.dashStyle ?? "Dash",
          width: plotLine.width ?? 2,
          label: plotLine.label
            ? {
                text: plotLine.label,
                align: "right",
                style: {
                  color: plotLine.color ?? (isDark ? "#64748b" : "#64748b"),
                  fontFamily: "IBM Plex Mono, monospace",
                },
              }
            : undefined,
        })),
      },
      legend: {
        enabled: legend.enabled && series.length > 1,
        align:
          legend.position === "left" || legend.position === "right"
            ? legend.position
            : "center",
        verticalAlign:
          legend.position === "top" || legend.position === "bottom"
            ? legend.position
            : "top",
        layout:
          legend.position === "left" || legend.position === "right"
            ? "vertical"
            : "horizontal",
        x: legend.position === "right" ? -10 : 0,
        y: legend.position === "top" ? 10 : 0,
      },
      plotOptions: {
        series: {
          // No `cursor` override: comments open on RIGHT-click (see the
          // contextmenu effect above), so the pointer shouldn't suggest a
          // left-click action on every point.
        },
        column: {
          grouping: true,
          pointWidth: Math.min(
            25,
            Math.max(15, 300 / Math.max(categories.length, 1)),
          ),
          borderRadius: isDark ? 3 : 2,
        },
        line: {
          lineWidth: 3,
          marker: {
            radius: 4,
            symbol: "circle",
          },
        },
        area: {
          fillOpacity: isDark ? 0.4 : 0.3,
          lineWidth: 2,
        },
      },
      series: series.map((s, index) => ({
        ...s,
        type: s.type || type,
        color: s.color ?? getSeriesColor(index),
      })) as any,
      tooltip: {
        enabled: true,
        useHTML: true,
        backgroundColor: isDark ? "#1e293b" : "#ffffff",
        borderColor: isDark ? "#475569" : "#e2e8f0",
        borderRadius: 8,
        shadow: true,
        style: {
          color: isDark ? "#e2e8f0" : "#1e293b",
          fontSize: "12px",
        },
        // Only use custom formatter if no specific tooltip format is provided
        ...(!tooltip.formatter &&
        !customOptions?.tooltip?.headerFormat &&
        !customOptions?.tooltip?.pointFormat
          ? {
              formatter: function () {
                // @ts-ignore - Highcharts tooltip context
                const prefix = tooltip.prefix ?? "";
                const suffix = tooltip.suffix ?? "";
                const seriesName = this.series.name;
                const value = this.y;
                // this.x is the category index (number) for category axes; resolve to the label
                const category =
                  (this as any).point?.category ??
                  categories[this.x as number] ??
                  String(this.x ?? "");

                return `<b>${seriesName}</b><br/>${category}: ${prefix}${value}${suffix}`;
              },
            }
          : {}),
        ...tooltip,
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              legend: {
                enabled: false,
              },
              yAxis: {
                labels: {
                  align: "left",
                  x: 0,
                  y: -5,
                },
                title: {
                  text: undefined, // changed from null to undefined
                },
              },
            },
          },
        ],
      },
      exporting: {
        enabled: exporting.enabled !== false,
        filename: exporting.filename || "chart",
        buttons: {
          contextButton: {
            menuItems: [
              "viewFullscreen",
              "separator",
              "downloadPNG",
              "downloadJPEG",
              "downloadPDF",
              "downloadSVG",
            ],
          },
        },
      },
      credits: {
        enabled: false,
      },
    });

    // Merge with custom options, but preserve essential zooming configuration
    const mergedOptions = deepMerge(themedConfig, customOptions);

    // Ensure mouse wheel zooming is always enabled if panZoom.mouseWheel is true
    if (panZoom.mouseWheel !== false && mergedOptions.chart) {
      if (!mergedOptions.chart.zooming) {
        mergedOptions.chart.zooming = {};
      }
      if (!mergedOptions.chart.zooming.mouseWheel) {
        mergedOptions.chart.zooming.mouseWheel = {};
      }
      mergedOptions.chart.zooming.mouseWheel.enabled = true;
      mergedOptions.chart.zooming.mouseWheel.sensitivity =
        mergedOptions.chart.zooming.mouseWheel.sensitivity || 1.1;

      // Ensure zooming type is set
      if (!mergedOptions.chart.zooming.type) {
        mergedOptions.chart.zooming.type = panZoom.type || "x";
      }
    }

    return mergedOptions;
  }, [
    type,
    title,
    subtitle,
    height,
    categories,
    yAxis,
    series,
    legend,
    tooltip,
    customOptions,
    panZoom,
    navigatorSelection,
    getChartConfig,
    getSeriesColor,
    isDark,
    commentProps,
    openPopover,
  ]);

  // Navigator chart options (mini chart below main chart)
  const navigatorOptions: Highcharts.Options = useMemo(() => {
    if (!themedNavigator.enabled) return {};

    return {
      chart: {
        type: type as any, // Use same chart type as main chart
        backgroundColor: "transparent",
        height: themedNavigator.height || 80,
        style: {
          fontFamily: "IBM Plex Sans, sans-serif",
        },
        margin: [5, 5, 20, 5],
        spacing: [0, 0, 0, 0],
        events: {
          selection: function (event: any) {
            if (event.xAxis && event.xAxis[0]) {
              const min = Math.round(event.xAxis[0].min);
              const max = Math.round(event.xAxis[0].max);

              // Ensure selection is within bounds
              const minBound = 0;
              const maxBound = categories.length - 1;

              const validMin = Math.max(minBound, Math.min(min, maxBound));
              const validMax = Math.max(minBound, Math.min(max, maxBound));

              if (validMin < validMax) {
                handleNavigatorSelection(validMin, validMax);
              }
              return false; // Prevent default zooming
            }
          },
          click: function (event: any) {
            // Allow clicking to set a single point selection
            const clickX = Math.round(event.xAxis[0].value);
            const halfWindow = Math.max(1, Math.floor(categories.length * 0.1)); // 10% of data as default window

            const min = Math.max(0, clickX - halfWindow);
            const max = Math.min(categories.length - 1, clickX + halfWindow);

            handleNavigatorSelection(min, max);
          },
        },
        zooming: {
          type: "x",
        },
      },
      title: { text: undefined },
      subtitle: { text: undefined },
      xAxis: {
        categories,
        labels: {
          enabled: false,
        },
        lineColor: statusColors.yAxisLabels,
        tickLength: 0,
        gridLineWidth: 0,
        min: 0,
        max: categories.length - 1,
        // Always show selection highlighting - use full range when no specific selection
        plotBands: [
          {
            from: navigatorSelection?.min ?? 0,
            to: navigatorSelection?.max ?? categories.length - 1,
            color: themedNavigator.maskFill || "rgba(37, 99, 235, 0.15)",
            borderColor: themedNavigator.outlineColor || "#2563eb",
            borderWidth: themedNavigator.outlineWidth || 2,
            className: "navigator-selection",
            zIndex: 3,
          },
        ],
        // Always show selection handles - use full range when no specific selection
        plotLines: [
          // Left handle
          {
            value: navigatorSelection?.min ?? 0,
            color: themedNavigator.handleColor || "#2563eb",
            width: 4,
            zIndex: 5,
            dashStyle: "Solid",
            className: "navigator-handle-left",
            label: {
              text: "⟨",
              style: {
                color: themedNavigator.handleColor || "#2563eb",
                fontSize: "14px",
                fontWeight: "bold",
                textShadow: isDark
                  ? "1px 1px 1px rgba(0,0,0,0.8)"
                  : "1px 1px 1px rgba(255,255,255,0.8)",
              },
              verticalAlign: "middle",
              align: "center",
              y: -2,
            },
          },
          // Right handle
          {
            value: navigatorSelection?.max ?? categories.length - 1,
            color: themedNavigator.handleColor || "#2563eb",
            width: 4,
            zIndex: 5,
            dashStyle: "Solid",
            className: "navigator-handle-right",
            label: {
              text: "⟩",
              style: {
                color: themedNavigator.handleColor || "#2563eb",
                fontSize: "14px",
                fontWeight: "bold",
                textShadow: isDark
                  ? "1px 1px 1px rgba(0,0,0,0.8)"
                  : "1px 1px 1px rgba(255,255,255,0.8)",
              },
              verticalAlign: "middle",
              align: "center",
              y: -2,
            },
          },
        ],
      },
      yAxis: {
        min: yAxis.min ?? 0,
        max: yAxis.max,
        title: { text: undefined },
        labels: { enabled: false },
        gridLineWidth: 0,
        lineWidth: 0,
        tickLength: 0,
      },
      legend: { enabled: false },
      plotOptions: {
        series: {
          enableMouseTracking: true,
          lineWidth: type === "line" ? 1 : undefined,
          marker: { enabled: false },
          states: {
            hover: {
              lineWidth: type === "line" ? 2 : undefined,
              brightness: 0.1,
            },
            inactive: {
              opacity: 0.8,
            },
          },
          point: {
            events: {
              click: function () {
                // @ts-ignore - Highcharts point context
                const clickX = this.x;
                const halfWindow = Math.max(
                  1,
                  Math.floor(categories.length * 0.1),
                );

                const min = Math.max(0, clickX - halfWindow);
                const max = Math.min(
                  categories.length - 1,
                  clickX + halfWindow,
                );

                handleNavigatorSelection(min, max);
              },
            },
          },
        },
        column: {
          grouping: true,
          pointWidth: Math.max(
            1,
            Math.min(4, 50 / Math.max(categories.length, 1)),
          ),
          borderRadius: 0,
          borderWidth: 0,
          groupPadding: 0.1,
          pointPadding: 0.05,
        },
        line: {
          lineWidth: 1,
          marker: { enabled: false },
        },
        area: {
          fillOpacity: 0.3,
          lineWidth: 1,
        },
      },
      series: series.map((s, index) => ({
        name: s.name,
        data: s.data,
        type: s.type || type, // Keep same type as main chart
        color: s.color ?? getSeriesColor(index),
        fillOpacity: type === "area" ? 0.3 : undefined,
        // Slightly transparent for navigator
        opacity: 0.7,
      })) as any,
      tooltip: {
        enabled: true,
        backgroundColor: isDark ? "rgba(15, 23, 42, 0.9)" : "rgba(0,0,0,0.8)",
        borderColor: themedNavigator.outlineColor || "#2563eb",
        borderRadius: 4,
        style: {
          color: "white",
          fontSize: "11px",
        },
        formatter: function () {
          // @ts-ignore - Highcharts tooltip context
          const cat =
            (this as any).point?.category ??
            categories[this.x as number] ??
            String(this.x ?? "");
          return `<b>${this.series.name}</b><br/>${cat}: ${this.y}${tooltip.suffix || ""}`;
        },
      },
      credits: { enabled: false },
    };
  }, [
    type,
    categories,
    yAxis,
    series,
    themedNavigator,
    navigatorSelection,
    handleNavigatorSelection,
    tooltip,
    getSeriesColor,
    isDark,
  ]);

  /**
   * Memoised chart element.
   *
   * `highcharts-react-official` calls `chart.update()` on every render of its
   * parent, so unrelated state here — opening or closing the comment popover,
   * toggling the data table — made the whole chart redraw. Returning the same
   * element reference lets React skip that subtree entirely unless the options
   * actually changed.
   */
  const mainChartElement = useMemo(
    () => (
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={mainChartRef}
        callback={(_chart: any) => {
          // Chart is now ready - trigger comment marker drawing
          setChartLoaded(true);
        }}
      />
    ),
    [chartOptions],
  );

  // Error state
  if (error) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <div className="text-red-500 font-medium mb-2">
            {t("charts.errorLoadingData")}
          </div>
          <div className="text-gray-500 text-sm mb-3">{error}</div>
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`} tabIndex={0}>
      {/* Loading overlay - only show when we have data and are refreshing */}
      {loading &&
        series.length > 0 &&
        series.some((s) => s.data.length > 0) && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
              <span className="text-gray-600 font-medium">
                {t("charts.loadingData")}
              </span>
            </div>
          </div>
        )}

      {/* Chart or no data message */}
      {series.length > 0 && series.some((s) => s.data.length > 0) ? (
        <>
          {/* Main Chart */}
          {mainChartElement}

          {/* Navigator Chart */}
          {themedNavigator.enabled && (
            <div style={{ marginTop: themedNavigator.margin || 20 }}>
              <div
                className={`border rounded-lg overflow-hidden shadow-sm ${themeClasses.borderPrimary} ${themeClasses.bgSecondary}`}
              >
                <div className="relative">
                  {/* Navigator Header */}
                  <div
                    className={`flex justify-between items-center px-3 py-2 border-b ${themeClasses.bgTertiary} ${themeClasses.borderPrimary}`}
                  >
                    <div className="flex items-center space-x-2">
                      <div
                        className={`text-xs font-medium ${themeClasses.textSecondary}`}
                      >
                        Navigator
                      </div>
                      <div className={`text-xs ${themeClasses.textMuted}`}>
                        •
                      </div>
                      <div className={`text-xs ${themeClasses.textMuted}`}>
                        {categories.length} points
                      </div>
                      {navigatorSelection && (
                        <>
                          <div className={`text-xs ${themeClasses.textMuted}`}>
                            •
                          </div>
                          <div
                            className={`text-xs px-2 py-1 rounded ${themeClasses.bgInfoSubtle} ${themeClasses.textInfo}`}
                          >
                            Use arrow keys to navigate
                          </div>
                        </>
                      )}
                    </div>
                    <div className={`text-xs ${themeClasses.textMuted}`}>
                      {navigatorSelection
                        ? `Range: ${categories[navigatorSelection.min] || navigatorSelection.min} - ${categories[navigatorSelection.max] || navigatorSelection.max}`
                        : `Full Range: ${categories[0] || 0} - ${categories[categories.length - 1] || categories.length - 1}`}
                    </div>
                  </div>

                  {/* Navigator Chart Area */}
                  <div
                    className={`relative cursor-crosshair ${themeClasses.bgPrimary}`}
                  >
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={navigatorOptions}
                      ref={navigatorChartRef}
                    />

                    {/* Overlay for better interaction feedback */}
                    <div className="absolute inset-0 pointer-events-none">
                      {navigatorSelection &&
                        navigatorSelection.min === 0 &&
                        navigatorSelection.max === categories.length - 1 && (
                          <div
                            className={`absolute inset-0 flex items-center justify-center ${isDark ? "bg-black bg-opacity-20" : "bg-black bg-opacity-5"}`}
                          >
                            <div
                              className={`text-xs px-2 py-1 rounded shadow-sm ${themeClasses.bgPrimary} ${themeClasses.textMuted}`}
                            >
                              Showing full range • Drag to zoom into specific
                              period
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Navigator Footer with Controls */}
                  <div className="px-3 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <div className="text-xs text-gray-500">
                          {series.length > 0 && categories.length > 0
                            ? `Showing ${series.length} series`
                            : "No data"}
                        </div>
                        {navigatorSelection && (
                          <div className="text-xs text-gray-600">
                            {navigatorSelection.min === 0 &&
                            navigatorSelection.max === categories.length - 1
                              ? `All ${categories.length} points selected`
                              : `${navigatorSelection.max - navigatorSelection.min + 1} of ${categories.length} points selected`}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {navigatorSelection &&
                          !(
                            navigatorSelection.min === 0 &&
                            navigatorSelection.max === categories.length - 1
                          ) && (
                            <>
                              <button
                                onClick={() => {
                                  // Expand selection by 20%
                                  const currentRange =
                                    navigatorSelection.max -
                                    navigatorSelection.min;
                                  const expansion = Math.max(
                                    1,
                                    Math.floor(currentRange * 0.2),
                                  );
                                  const newMin = Math.max(
                                    0,
                                    navigatorSelection.min - expansion,
                                  );
                                  const newMax = Math.min(
                                    categories.length - 1,
                                    navigatorSelection.max + expansion,
                                  );
                                  handleNavigatorSelection(newMin, newMax);
                                }}
                                className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 transition-colors"
                                title="Expand selection"
                              >
                                ⟵⟶
                              </button>
                              <button
                                onClick={() => {
                                  // Contract selection by 20%
                                  const currentRange =
                                    navigatorSelection.max -
                                    navigatorSelection.min;
                                  const contraction = Math.max(
                                    1,
                                    Math.floor(currentRange * 0.2),
                                  );
                                  const newMin =
                                    navigatorSelection.min + contraction;
                                  const newMax =
                                    navigatorSelection.max - contraction;
                                  if (newMin < newMax) {
                                    handleNavigatorSelection(newMin, newMax);
                                  }
                                }}
                                className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 transition-colors"
                                title="Contract selection"
                              >
                                ⟶⟵
                              </button>
                            </>
                          )}
                        {navigatorSelection &&
                          !(
                            navigatorSelection.min === 0 &&
                            navigatorSelection.max === categories.length - 1
                          ) && (
                            <button
                              onClick={() => {
                                const fullRange = {
                                  min: 0,
                                  max: categories.length - 1,
                                };
                                setNavigatorSelection(fullRange);
                                onSelectionChange?.(0, categories.length - 1);
                                // Reset main chart zoom
                                if (mainChartRef.current) {
                                  const chart = mainChartRef.current.chart;
                                  if (chart && chart.xAxis && chart.xAxis[0]) {
                                    chart.xAxis[0].setExtremes(
                                      null,
                                      null,
                                      true,
                                      false,
                                    );
                                  }
                                }
                              }}
                              className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              title="Reset to show all data"
                            >
                              Show All
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Controls row: extra controls left, action buttons right */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {extraControls && <div className="mr-auto">{extraControls}</div>}
            <div className="flex items-center gap-1.5 ml-auto">
              <button
                onClick={exportToCSV}
                className="px-2 sm:px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-xs font-medium"
                title="Export raw data as CSV file"
              >
                CSV
              </button>
              <button
                onClick={exportToExcel}
                className="px-2 sm:px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-xs font-medium"
                title="Export raw data as Excel file"
              >
                Excel
              </button>
            </div>
            <button
              onClick={() => setShowData((v) => !v)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-xs sm:text-sm font-medium"
            >
              {showData
                ? t("charts.hideData") || "Hide Data"
                : t("charts.showData") || "Show Data"}
            </button>
          </div>

          {/* Data Grid */}
          {showData && (
            <div className="overflow-x-auto mt-2">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead style={{ backgroundColor: "var(--bg-secondary)" }}>
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      Category
                    </th>
                    {series.map((s, idx) => (
                      <th
                        key={idx}
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        {s.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody
                  className="divide-y divide-gray-200 dark:divide-gray-700"
                  style={{ backgroundColor: "var(--bg-primary)" }}
                >
                  {categories.map((category, rowIdx) => (
                    <tr key={rowIdx}>
                      <td
                        className="px-4 py-3 whitespace-nowrap text-sm"
                        style={{ color: "var(--text-primary)" }}
                      >
                        {category}
                      </td>
                      {series.map((s, colIdx) => {
                        const value = s.data[rowIdx];
                        return (
                          <td
                            key={colIdx}
                            className="px-4 py-3 whitespace-nowrap text-sm font-mono"
                            style={{ color: "var(--text-primary)" }}
                          >
                            {value !== null && value !== undefined
                              ? value
                              : "-"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : loading ? (
        <div
          className="flex flex-col items-center justify-center"
          style={{ height }}
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
            <span className="text-gray-500 font-medium">
              {t("charts.loadingData")}
            </span>
          </div>
          {/* Skeleton bars */}
          <div className="flex items-end justify-center space-x-2 mt-6 opacity-30">
            <div className="w-8 h-16 bg-gray-300 rounded animate-pulse"></div>
            <div
              className="w-8 h-24 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.1s" }}
            ></div>
            <div
              className="w-8 h-20 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.2s" }}
            ></div>
            <div
              className="w-8 h-32 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.3s" }}
            ></div>
            <div
              className="w-8 h-28 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.4s" }}
            ></div>
            <div
              className="w-8 h-18 bg-gray-300 rounded animate-pulse"
              style={{ animationDelay: "0.5s" }}
            ></div>
          </div>
        </div>
      ) : (
        <div
          className="flex items-center justify-center text-gray-500"
          style={{ height }}
        >
          <div className="text-center">
            <div className="text-lg font-medium mb-2">
              {t("charts.noDataAvailable")}
            </div>
            <div className="text-sm">{noDataMessage}</div>
          </div>
        </div>
      )}

      {/* Comment popover — appears on point click */}
      {popoverPoint && commentProps?.chartType && (
        <Suspense fallback={null}>
          <ChartCommentPopover
            chartType={commentProps.chartType}
            machineBK={commentProps.machineBK}
            point={popoverPoint}
            onClose={() => {
              closePopover();
              refetchComments();
            }}
            containerRef={containerRef}
          />
        </Suspense>
      )}
    </div>
  );
};

export default BaseChart;
